import { getPerformanceInstance } from '@/lib/firebase-config';
import logger from '@/lib/logging';

export interface PerformanceTrace {
  stop: (metrics?: Record<string, number>, attributes?: Record<string, string>) => void;
  recordMetric: (metricName: string, value: number) => void;
  putAttribute: (attrName: string, value: string) => void;
}

const activeTraces = new Map<string, any>();

/**
 * Starts a Firebase Performance custom trace safely in client browser.
 */
export async function startSelfAssessmentTrace(traceName: string): Promise<PerformanceTrace | null> {
  if (typeof window === 'undefined') return null;

  try {
    const perf = await getPerformanceInstance();
    if (!perf) return null;

    const { trace } = await import('firebase/performance');
    const t = trace(perf, traceName);
    t.start();
    activeTraces.set(traceName, t);

    logger.debug(`[⚡ Perf] Started trace: ${traceName}`);

    return {
      stop: (metrics, attributes) => {
        try {
          if (metrics) {
            Object.entries(metrics).forEach(([k, v]) => t.putMetric(k, v));
          }
          if (attributes) {
            Object.entries(attributes).forEach(([k, v]) => t.putAttribute(k, v));
          }
          t.stop();
          activeTraces.delete(traceName);
          logger.debug(`[⚡ Perf] Stopped trace: ${traceName}`, { metrics, attributes });
        } catch (err) {
          logger.warn(`[⚡ Perf] Error stopping trace ${traceName}:`, err);
        }
      },
      recordMetric: (metricName, value) => {
        try {
          t.putMetric(metricName, value);
        } catch (err) {
          logger.warn(`[⚡ Perf] Error putting metric ${metricName} on trace ${traceName}:`, err);
        }
      },
      putAttribute: (attrName, value) => {
        try {
          t.putAttribute(attrName, value);
        } catch (err) {
          logger.warn(`[⚡ Perf] Error putting attribute ${attrName} on trace ${traceName}:`, err);
        }
      },
    };
  } catch (err) {
    logger.debug(`[⚡ Perf] Safe fallback - trace ${traceName} skipped:`, err);
    return null;
  }
}

/**
 * Stops an active trace by name if running.
 */
export function stopSelfAssessmentTrace(
  traceName: string,
  metrics?: Record<string, number>,
  attributes?: Record<string, string>
) {
  const t = activeTraces.get(traceName);
  if (t) {
    try {
      if (metrics) {
        Object.entries(metrics).forEach(([k, v]) => t.putMetric(k, v));
      }
      if (attributes) {
        Object.entries(attributes).forEach(([k, v]) => t.putAttribute(k, v));
      }
      t.stop();
      activeTraces.delete(traceName);
      logger.debug(`[⚡ Perf] Stopped active trace: ${traceName}`, { metrics, attributes });
    } catch (err) {
      logger.warn(`[⚡ Perf] Error stopping trace ${traceName}:`, err);
    }
  }
}

/**
 * Measures the execution time of an asynchronous function using Firebase Performance.
 */
export async function measureAsyncStep<T>(
  traceName: string,
  fn: () => Promise<T>,
  attributes?: Record<string, string>
): Promise<T> {
  const trace = await startSelfAssessmentTrace(traceName);
  const startTime = Date.now();
  try {
    const result = await fn();
    const duration = Date.now() - startTime;
    if (trace) {
      trace.stop({ duration_ms: duration }, attributes);
    }
    return result;
  } catch (error) {
    const duration = Date.now() - startTime;
    if (trace) {
      trace.stop(
        { duration_ms: duration, error_count: 1 },
        { ...attributes, status: 'error' }
      );
    }
    throw error;
  }
}
